/* NWS Personalize — Tier 2 global helper
   Reads localStorage.nws_vertical and:
   - Adds body class `nws-v-<vertical>`
   - Hides elements with [data-vertical-only] that don't include current vertical
   - Surfaces "your view" chip via [data-personalize-chip] target
   - Exposes window.NWSPersonalize.apply() for late-loaded sections
*/
(function() {
  const STORE_KEY = 'nws_vertical';
  const STORE_LABEL = 'nws_vertical_label';

  function getVertical() {
    return localStorage.getItem(STORE_KEY);
  }
  function getLabel() {
    return localStorage.getItem(STORE_LABEL) || getVertical() || '';
  }

  function apply() {
    const v = getVertical();
    const label = getLabel();
    const body = document.body;
    if (!body) return;

    // Reset prior classes
    Array.from(body.classList).forEach(c => {
      if (c.startsWith('nws-v-')) body.classList.remove(c);
    });
    if (v) body.classList.add('nws-v-' + v);

    // Hide/show by [data-vertical-only="x,y"] (csv); empty/missing = always show
    document.querySelectorAll('[data-vertical-only]').forEach(el => {
      const allowed = (el.dataset.verticalOnly || '').split(',').map(s => s.trim()).filter(Boolean);
      if (!v || v === 'all' || allowed.length === 0 || allowed.includes(v)) {
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    // Render chips
    document.querySelectorAll('[data-personalize-chip]').forEach(el => {
      if (!v || v === 'all') {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.innerHTML = `
        <span class="nws-chip__dot" aria-hidden="true"></span>
        <span class="nws-chip__label">Curated for: <strong>${escapeHtml(label)}</strong></span>
        <button type="button" class="nws-chip__reset" aria-label="Reset view">Change</button>
      `;
      const btn = el.querySelector('.nws-chip__reset');
      if (btn && !btn.dataset.bound) {
        btn.dataset.bound = '1';
        btn.addEventListener('click', () => {
          localStorage.removeItem(STORE_KEY);
          localStorage.removeItem(STORE_LABEL);
          localStorage.removeItem('nws_shortlist');
          window.location.reload();
        });
      }
    });

    // Reorder containers with [data-vertical-priority]:
    // child elements get reordered to put data-vertical="<v>" first.
    document.querySelectorAll('[data-vertical-priority]').forEach(container => {
      if (!v || v === 'all') return;
      const items = Array.from(container.children);
      const matches = items.filter(el => (el.dataset.vertical || '').split(',').includes(v));
      const rest = items.filter(el => !(el.dataset.vertical || '').split(',').includes(v));
      [...matches, ...rest].forEach(el => container.appendChild(el));
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[c]));
  }

  // ---------- Reveal-on-scroll helper ----------
  // Any element with [data-reveal] gets data-revealed="true" when it scrolls into view.
  // Optional [data-reveal-delay="120"] for stagger.
  function initReveal() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach(el => { el.dataset.revealed = 'true'; });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const delay = parseInt(el.dataset.revealDelay || '0', 10);
          setTimeout(() => { el.dataset.revealed = 'true'; }, delay);
          io.unobserve(el);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('[data-reveal]:not([data-revealed])').forEach(el => io.observe(el));
  }

  // ---------- Counter-on-scroll helper ----------
  // Any element [data-count-to="500"] [data-count-duration="900"] animates up when in view.
  function initCounters() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-count-to]').forEach(el => { el.textContent = el.dataset.countTo; });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseInt(el.dataset.countTo, 10) || 0;
        const dur = parseInt(el.dataset.countDuration || '900', 10);
        const start = performance.now();
        function tick(now) {
          const t = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - t, 3);
          el.textContent = Math.round(target * eased).toLocaleString();
          if (t < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { threshold: 0.4 });
    document.querySelectorAll('[data-count-to]').forEach(el => io.observe(el));
  }

  function initAll() { apply(); initReveal(); initCounters(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.NWSPersonalize = { apply: apply, getVertical: getVertical, getLabel: getLabel, initReveal: initReveal, initCounters: initCounters };
})();
